import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import { teamMembersKey } from './useTeamMembers'
import { toAppError, type AppError } from '../lib/errors'

/**
 * Team-join-request hooks — owner/admin surface for triaging pending
 * requests from domain-matching corporate signups who didn't land in
 * auto-join mode.
 *
 * The list query calls `get_team_join_requests` (SECURITY DEFINER, added
 * in 2026-05-17-followup-hardening.sql §3). A direct PostgREST embed
 * `user:profiles(...)` returns null for cross-user rows because the
 * profiles RLS policy is self-only — the RPC does the join server-side
 * under definer privileges and returns flat `user_email` /
 * `user_display_name` columns alongside the request fields.
 *
 * Approve / decline remain SECURITY DEFINER RPCs because both mutate
 * multiple tables (team_members + team_invitations + audit_log) in the
 * approve path.
 *
 * Types are hand-written — `team_join_requests` the table and the
 * approve/decline/list RPCs aren't in `database.types.ts` yet (types are
 * regenerated post-merge per team convention). Matches the `UntypedRpc`
 * cast pattern in useDiscovery.ts.
 */

// ──────────────────────────────────────────────────────────────────────────
// Untyped-client cast — just the rpc surface now.
// ──────────────────────────────────────────────────────────────────────────

type UntypedRpc = {
  rpc: (
    fn: string,
    args?: Record<string, unknown>,
  ) => Promise<{ data: unknown; error: { message: string } | null }>
}

const rpc = (): UntypedRpc => supabase as unknown as UntypedRpc

export interface TeamJoinRequestWithUser {
  id: string
  team_id: string
  user_id: string
  requested_at: string
  decided_at: string | null
  decision: 'pending' | 'approved' | 'declined' | 'expired'
  decided_by: string | null
  expires_at: string
  user_email: string | null
  user_display_name: string | null
}

export const teamJoinRequestsKey = (teamId: string | undefined): QueryKey =>
  ['teamJoinRequests', teamId ?? 'none']

/**
 * Pending join requests for a team. Rows include the requester's email
 * and display_name resolved server-side — see module docstring.
 */
export function useTeamJoinRequests(teamId: string | null | undefined) {
  const key = teamJoinRequestsKey(teamId ?? undefined)
  const query = useQuery<TeamJoinRequestWithUser[]>({
    queryKey: key,
    enabled: !!teamId,
    queryFn: async () => {
      const { data, error } = await rpc().rpc('get_team_join_requests', {
        p_team_id: teamId!,
      })
      if (error) throw new Error(error.message)
      return ((data as TeamJoinRequestWithUser[] | null) ?? [])
    },
  })
  return {
    requests: query.data ?? [],
    loading: !!teamId && query.isPending,
    error: query.error ? toAppError(query.error) : null as AppError | null,
  }
}

/**
 * Approve a pending join request. Server-side the RPC:
 *   - inserts the team_members row
 *   - flips the requester's current_team_id
 *   - marks the request approved
 *   - revokes sibling pending invitations for the same email
 *   - fires the "welcome" email via pg_net (non-blocking)
 *
 * Invalidates both the requests list (to drop the row) and the
 * team members list (to show the newcomer).
 */
export function useApproveJoinRequest(teamId: string | null | undefined) {
  const qc = useQueryClient()
  return useMutation<void, Error, string>({
    mutationKey: ['teamJoinRequests', 'approve', teamId],
    mutationFn: async (requestId: string) => {
      const { error } = await rpc().rpc('approve_join_request', {
        p_request_id: requestId,
      })
      if (error) throw new Error(error.message)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: teamJoinRequestsKey(teamId ?? undefined) })
      qc.invalidateQueries({ queryKey: teamMembersKey(teamId ?? undefined) })
    },
  })
}

/**
 * Decline a pending join request. No membership side-effect; just marks
 * the request declined. The requester can re-request later (or be invited
 * directly).
 */
export function useDeclineJoinRequest(teamId: string | null | undefined) {
  const qc = useQueryClient()
  return useMutation<void, Error, string>({
    mutationKey: ['teamJoinRequests', 'decline', teamId],
    mutationFn: async (requestId: string) => {
      const { error } = await rpc().rpc('decline_join_request', {
        p_request_id: requestId,
      })
      if (error) throw new Error(error.message)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: teamJoinRequestsKey(teamId ?? undefined) })
    },
  })
}
